import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx'; 

// --- Layout Components ---
import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';

// --- Complete list of page imports from your original file ---
import Dashboard from './pages/Dashboard';
import AddGroceryProduct from './pages/grocery/AddGroceryProduct';
import LoginPage from './pages/LoginPage';
import UsersPage from './pages/UsersPage';
import RolesPage from './pages/RolesPage.jsx';
import AddRolePage from './pages/AddRolePage.jsx';
import AddEditUserPage from './pages/AddEditUserPage';
import Customers from './pages/Customers.jsx';
import ChefsPage from './pages/ChefsPage';
import ProductsPage from './pages/ProductsPage';
import AddProductPage from './pages/AddProductPage';
import EditProductPage from './pages/EditProductPage';
import CategoriesPage from './pages/grocery/CategoriesPage';
import AddCategoryPage from './pages/grocery/AddCategoryPage'; // Assuming this is the correct path
import FoodItemsPage from './pages/FoodItemsPage';
import AddFoodItemPage from './pages/AddFoodItemPage';
import PackagesPage from './pages/PackagesPage';
import AddPackagePage from './pages/AddPackagePage';
import GroceryProductList from './pages/grocery/GroceryProductList';
import PartyProductList from './pages/party/PartyProductList';
import PartyCategories from './pages/party/PartyCategories';
import GoodsReceivedNotesPage from './pages/GoodsReceivedNotesPage';
import AddGrnPage from './pages/AddGrnPage';
import SubscribersList from './pages/SubscribersList';
import PackageRequests from './pages/PackageRequests';
import ApprovedAddons from './pages/ApprovedAddons';
import AddonRequest from './pages/AddonRequest';
import HubView from './pages/HubView';
import HubRequests from './pages/HubRequests';
import CreateHub from './pages/CreateHub';
import AssignDelivery from './pages/AssignDelivery';
import DeliveryPersonManagement from './pages/DeliveryPersonManagement';
import Wallet from './pages/Wallet';
import KitchenDashboard from './pages/KitchenDashboard';
import CookingRequest from './pages/CookingRequest';
import ExpensesPage from './pages/ExpensesPage.jsx';
import AddExpensePage from './pages/AddExpensePage.jsx';
import Banner from './pages/grocery/banner';
import BannersParty from './pages/party/BannersParty.jsx';
import LocationPage from './pages/LocationPage';
import TomorrowsMealsPage from './pages/TomorrowsMealsPage.jsx';

// --- The clean, prop-less layout component ---
const AppLayout = () => (
  <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
    <Sidebar />
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Topbar /> 
      <main style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f0f2f5', padding: '1.5rem' }}>
        <Outlet /> 
      </main>
    </div>
  </div>
);

// --- The single, powerful route protection component ---
const ProtectedRoute = ({ children, requiredPermission }) => {
  const { user, hasPermission } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};


// --- The Main Application Router ---
function App() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      
      {/* All authenticated routes are nested inside the AppLayout */}
      <Route path="/" element={<AppLayout />}>
        
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* User Management */}
        <Route path="users" element={<ProtectedRoute requiredPermission="users.view"><UsersPage /></ProtectedRoute>} />
        <Route path="users/add" element={<ProtectedRoute requiredPermission="users.create"><AddEditUserPage /></ProtectedRoute>} />
        <Route path="users/edit/:id" element={<ProtectedRoute requiredPermission="users.update"><AddEditUserPage /></ProtectedRoute>} />
        <Route path="roles" element={<ProtectedRoute requiredPermission="roles.view"><RolesPage /></ProtectedRoute>} />
        <Route path="roles/add" element={<ProtectedRoute requiredPermission="roles.create"><AddRolePage /></ProtectedRoute>} />
        
        {/* Contacts */}
        <Route path="customers" element={<ProtectedRoute requiredPermission="customers.view"><Customers /></ProtectedRoute>} />
        <Route path="chefs" element={<ProtectedRoute requiredPermission="chefs.view"><ChefsPage /></ProtectedRoute>} />
        
        {/* Products */}
        <Route path="product" element={<ProtectedRoute requiredPermission="raw_products.view"><ProductsPage /></ProtectedRoute>} />
        <Route path="add" element={<ProtectedRoute requiredPermission="raw_products.create"><AddProductPage /></ProtectedRoute>} />
        <Route path="edit/:id" element={<ProtectedRoute requiredPermission="raw_products.update"><EditProductPage /></ProtectedRoute>} />
        
        {/* Categories (with new add/edit routes) */}
        <Route path="categories" element={<ProtectedRoute requiredPermission="categories.view"><CategoriesPage /></ProtectedRoute>} />
        <Route path="categories/add" element={<ProtectedRoute requiredPermission="categories.create"><AddCategoryPage /></ProtectedRoute>} />
        <Route path="categories/edit/:categoryId" element={<ProtectedRoute requiredPermission="categories.update"><AddCategoryPage /></ProtectedRoute>} />
        <Route path="partycategories" element={<ProtectedRoute requiredPermission="categories.view"><PartyCategories /></ProtectedRoute>} />
        
        {/* Food & Packages */}
        <Route path="food-items" element={<ProtectedRoute requiredPermission="food_items.view"><FoodItemsPage /></ProtectedRoute>} />
        <Route path="food-items/add" element={<ProtectedRoute requiredPermission="food_items.create"><AddFoodItemPage /></ProtectedRoute>} />
        <Route path="food-items/edit/:foodItemId" element={<ProtectedRoute requiredPermission="food_items.update"><AddFoodItemPage /></ProtectedRoute>} />
        <Route path="packages" element={<ProtectedRoute requiredPermission="packages.view"><PackagesPage /></ProtectedRoute>} />
        <Route path="packages/add" element={<ProtectedRoute requiredPermission="packages.create"><AddPackagePage /></ProtectedRoute>} />
        <Route path="packages/edit/:packageId" element={<ProtectedRoute requiredPermission="packages.update"><AddPackagePage /></ProtectedRoute>} />
        
        {/* Inventory */}
        <Route path="grns" element={<ProtectedRoute requiredPermission="inventory.view"><GoodsReceivedNotesPage /></ProtectedRoute>} />
        <Route path="grns/add" element={<ProtectedRoute requiredPermission="inventory.create"><AddGrnPage /></ProtectedRoute>} />
        
        {/* Subscriptions */}
        <Route path="subscribers" element={<ProtectedRoute requiredPermission="subscriptions.view"><SubscribersList /></ProtectedRoute>} />
        <Route path="package-requests" element={<ProtectedRoute requiredPermission="subscriptions.manage"><PackageRequests /></ProtectedRoute>} />
        
        {/* Addons Routes - Add permissions if needed */}
        <Route path="approved-addons" element={<ProtectedRoute><ApprovedAddons /></ProtectedRoute>} />
        <Route path="addon-requests" element={<ProtectedRoute><AddonRequest /></ProtectedRoute>} />
        
        {/* Hub Management */}
        <Route path="hub-view" element={<ProtectedRoute requiredPermission="hubs.view"><HubView /></ProtectedRoute>} />
        <Route path="hub-requests" element={<ProtectedRoute requiredPermission="hubs.manage"><HubRequests /></ProtectedRoute>} />
        <Route path="create-hub" element={<ProtectedRoute requiredPermission="hubs.create"><CreateHub /></ProtectedRoute>} />
        
        {/* Delivery */}
        <Route path="assign-delivery" element={<ProtectedRoute requiredPermission="delivery.assign"><AssignDelivery /></ProtectedRoute>} />
        <Route path="delivery-person" element={<ProtectedRoute requiredPermission="delivery_person.manage"><DeliveryPersonManagement /></ProtectedRoute>} />
        
        {/* Kitchen */}
        <Route path="kitchen-dashboard" element={<ProtectedRoute requiredPermission="kitchen.dashboard"><KitchenDashboard /></ProtectedRoute>} />
        <Route path="cooking-requests" element={<ProtectedRoute requiredPermission="kitchen.requests"><CookingRequest /></ProtectedRoute>} />
        
        {/* Banners */}
        <Route path="banner" element={<ProtectedRoute requiredPermission="banner.manage"><Banner /></ProtectedRoute>} />
        <Route path="banners-party" element={<ProtectedRoute><BannersParty /></ProtectedRoute>} />
        
        {/* Meals */}
        <Route path="tomorrows-meals" element={<ProtectedRoute requiredPermission="meals.view"><TomorrowsMealsPage /></ProtectedRoute>} />
        
        {/* Expenses */}
        <Route path="expenses" element={<ProtectedRoute requiredPermission="expenses.view"><ExpensesPage /></ProtectedRoute>} />
        <Route path="expenses/add" element={<ProtectedRoute requiredPermission="expenses.create"><AddExpensePage /></ProtectedRoute>} />
        
        {/* Routes that did not have specific permissions can be wrapped without a permission prop */}
        <Route path="wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
        <Route path="location" element={<ProtectedRoute><LocationPage /></ProtectedRoute>} />
        <Route path="groceryproduct" element={<ProtectedRoute><GroceryProductList /></ProtectedRoute>} />
        <Route path="partyproduct" element={<ProtectedRoute><PartyProductList /></ProtectedRoute>} />
      </Route>
      <Route path="groceryproduct" element={<ProtectedRoute requiredPermission="grocery_products.view"><GroceryProductList /></ProtectedRoute>} />
<Route path="groceryproduct/add" element={<ProtectedRoute requiredPermission="grocery_products.create"><AddGroceryProduct /></ProtectedRoute>} />
<Route path="groceryproduct/edit/:productId" element={<ProtectedRoute requiredPermission="grocery_products.update"><AddGroceryProduct /></ProtectedRoute>} />

      {/* Fallback route */}
      <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
    </Routes>
  );
}

export default App;